// biome-ignore-all lint/suspicious/useAwait: this is a thunk
import type {
	ExerciseInfoId,
	MetricCanonicalName,
	SectionPlanInfoId,
	SectionType,
	SetPlanInfo,
	SetPlanInfoId,
	StepPlanInfo,
	StepPlanInfoId,
	WorkoutPlanInfoId,
} from '@az3fr/core';
import { createID } from '@az3fr/core';
import type { Update } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';

import type { ThunkResult } from '@/features/workout-creator/model/workoutCreator.types';
import type { RootState } from '@/lib/store';
import { sectionPlanSliceSelectors } from '@/entities/section-plan/model/section-plan.selector';
import {
	createNewSectionPlan,
	sectionPlanSliceActions,
} from '@/entities/section-plan/model/section-plan.slice';
import { sectionTypeDisplayInfos } from '@/entities/section-plan/model/section-type';
import { setPlanSliceSelectors } from '@/entities/set-plan/model/set-plan.selector';
import { createNewSetPlan, setPlanSliceActions } from '@/entities/set-plan/model/set-plan.slice';
import { stepPlanSliceSelectors } from '@/entities/step-plan/model/step-plan.selector';
import {
	createNewStepPlan,
	stepPlanSliceActions,
} from '@/entities/step-plan/model/step-plan.slice';
import { workoutPlanSliceActions } from '@/entities/workout-plan/model/workout-plan.slice';
import { workoutCreatorSliceActions } from '@/features/workout-creator/model/workoutCreator.slice';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Helper function to create set plans with proper set numbering
 * Calculates the next set number based on existing sets for a step
 * Returns complete SetPlanInfo objects ready for dispatch
 */
function getNextSetPlanData(
	state: RootState,
	stepPlanId: StepPlanInfoId,
): { nextSetNumber: number; totalSets: number } {
	const existingSets = setPlanSliceSelectors.selectAllByStepPlanId(state, stepPlanId);

	const lastInsertedSetNumber =
		existingSets.length > 0 ? Math.max(...existingSets.map((s) => s.setNumber)) : 0;

	return {
		nextSetNumber: lastInsertedSetNumber + 1,
		totalSets: existingSets.length,
	};
}

/**
 * Helper function to create step plans with proper orderInSectionPlan numbering
 * Calculates the next orderInSectionPlan based on existing steps for a section
 * Returns complete StepPlanInfo objects ready for dispatch
 */
function getNextStepPlanData(
	state: RootState,
	sectionPlanId: SectionPlanInfoId,
): { nextStepOrderInSectionPlan: number; totalSteps: number } {
	const existingSteps = stepPlanSliceSelectors.selectAllBySectionPlanId(state, sectionPlanId);

	const lastInsertedStepOrderInSectionPlan =
		existingSteps.length > 0 ? Math.max(...existingSteps.map((s) => s.orderInSectionPlan)) : 0;

	return {
		nextStepOrderInSectionPlan: lastInsertedStepOrderInSectionPlan + 1,
		totalSteps: existingSteps.length,
	};
}

// ============================================================================
// Plan Lifecycle Thunks
// ============================================================================

/**
 * Initializes a new, empty workout plan draft
 *
 * @todo Server Integration:
 * - Call useCreateDraftWorkoutPlan mutation (POST /api/workout-plans)
 * - Handle server response with proper workout plan structure
 * - Map server response to Redux state
 * - Handle network errors and retry logic
 *
 * @todo Future Enhancements:
 * - Add plan templates/presets
 * - Validate plan name uniqueness
 * - Add user authentication check
 */
const startNewWorkoutPlan = createAsyncThunk<
	ThunkResult<WorkoutPlanInfoId>, // Returns the new plan ID
	{ name?: string },
	{ state: RootState }
>('features/workoutCreator/startNewWorkoutPlan', async (payload, { dispatch }) => {
	try {
		// Generate temporary plan ID (will be replaced by server ID later)
		// @todo: this should be created using a temp id generator function.
		const planId = createID('workoutPlan');

		// 1. Create new workout plan entity
		dispatch(
			workoutPlanSliceActions.addedOne({
				id: planId,
				name: payload.name || '',
			}),
		);

		// 2. Set as active plan in workout creator
		dispatch(workoutCreatorSliceActions.activePlanIdSet(planId));
		dispatch(workoutCreatorSliceActions.dirtyFlagSet(false)); // New plan starts clean

		return {
			success: true,
			data: planId,
		};
	} catch (error) {
		return {
			success: false,
			error: `Failed to create new plan: ${error instanceof Error ? error.message : 'Unknown error'}`,
		};
	}
});

/**
 * Updates the properties of a workout plan
 *
 * @todo Server Integration:
 * - Call useUpdateWorkoutPlan mutation (PATCH /api/workout-plans/:id)
 * - Handle server response with proper workout plan structure
 */
const updateWorkoutPlanProperties = createAsyncThunk<
	ThunkResult<void>,
	{
		workoutPlanId: WorkoutPlanInfoId;
		changes: { name?: string; description?: string };
	},
	{ state: RootState }
>('features/workoutCreator/updateWorkoutPlanProperties', async (payload, { dispatch }) => {
	dispatch(
		workoutPlanSliceActions.updatedOne({
			id: payload.workoutPlanId,
			changes: payload.changes,
		}),
	);

	return {
		success: true,
		data: undefined,
	};
});

/**
 * Persists the entire current state of the draft plan
 *
 * @todo Server Integration:
 * - Check workoutCreator.isDirty flag - exit early if false
 * - Use selector to get full populated workout plan
 * - Call useUpdateWorkoutPlan mutation (PATCH /api/workout-plans/:id)
 * - Handle ID mapping (temp → permanent IDs from server)
 * - Re-hydrate store with server response
 * - Handle optimistic updates and rollback on failure
 *
 * @todo Auto-save Implementation:
 * - Add debouncing logic (2 second delay)
 * - Add periodic save (every 30 seconds)
 * - Handle offline scenarios
 */
const saveDraftWorkoutPlan = createAsyncThunk<
	ThunkResult<void>,
	{ workoutPlanId: WorkoutPlanInfoId },
	{ state: RootState }
>('features/workoutCreator/saveDraftWorkoutPlan', async (_, { dispatch, getState }) => {
	try {
		const state = getState();

		// Early exit if no changes to save
		if (state.workoutCreator.isDirty === false) {
			return {
				success: true,
				data: undefined,
			};
		}

		dispatch(workoutCreatorSliceActions.saveStatusSet('saving'));

		// @todo: Get full populated workout plan using selector
		// @todo: Call server API to save
		// @todo: Handle ID mapping response
		// @todo: Re-hydrate entities with permanent IDs

		// Simulate save for now
		await new Promise((resolve) => setTimeout(resolve, 1000));

		dispatch(workoutCreatorSliceActions.saveStatusSet('saved'));

		return {
			success: true,
			data: undefined,
		};
	} catch (error) {
		dispatch(workoutCreatorSliceActions.saveStatusSet('error'));
		return {
			success: false,
			error: `Failed to save draft: ${error instanceof Error ? error.message : 'Unknown error'}`,
		};
	}
});

/**
 * Performs final validation and publishes the plan
 *
 * @todo Server Integration:
 * - First call saveDraft() to ensure latest state
 * - Call usePublishPlan mutation (POST /api/workout-plans/:id/publish)
 * - Server runs full validation (validateSectionPlan, etc.)
 * - Handle validation errors from server
 * - Update plan status to 'active' on success
 *
 * @todo Validation:
 * - Add client-side pre-validation before server call
 * - Check minimum plan requirements (at least one section)
 * - Validate all exercises are properly configured
 */
const publishWorkoutPlan = createAsyncThunk<
	ThunkResult<WorkoutPlanInfoId>,
	{ workoutPlanId: WorkoutPlanInfoId },
	{ state: RootState }
>('features/workoutCreator/publishWorkoutPlan', async (payload) => {
	try {
		// @todo: First call saveDraft() to ensure server has latest state
		// @todo: Call server API for publishing
		// @todo: Handle server validation errors
		// @todo: Update plan status

		// Simulate publish for now
		await new Promise((resolve) => setTimeout(resolve, 1500));

		return {
			success: true,
			data: payload.workoutPlanId,
		};
	} catch (error) {
		return {
			success: false,
			error: `Failed to publish plan: ${error instanceof Error ? error.message : 'Unknown error'}`,
		};
	}
});

// ============================================================================
// Section Management Thunks
// ============================================================================

/**
 * Adds a new, empty section to the current workout plan
 *
 * @todo Server Integration:
 * - Call useCreateSectionPlan mutation (POST /api/workout-plans/:id/section-plans)
 * - Handle server response with proper section plan structure
 * - Map server response to Redux state
 * - Handle network errors and retry logic
 *
 * @todo Business Logic:
 * - Add section ordering validation
 * - Check plan-level section limits
 */
const addSectionPlan = createAsyncThunk<
	ThunkResult<SectionPlanInfoId>,
	{ workoutPlanId: WorkoutPlanInfoId; sectionType: SectionType },
	{ state: RootState }
>(
	'features/workoutCreator/addSectionPlan',
	async ({ workoutPlanId, sectionType }, { dispatch, getState }) => {
		try {
			const state = getState();

			// 1. Calculate the order
			const existingSections = sectionPlanSliceSelectors.selectAllByWorkoutPlanId(
				state,
				workoutPlanId,
			);

			// @todo: take care of decimal part
			const orderInWorkoutPlan =
				existingSections.length > 0
					? existingSections[existingSections.length - 1].orderInWorkoutPlan + 1
					: 1;

			// 2. Create the new section object using the factory function
			const newSection = createNewSectionPlan({
				workoutPlanId,
				sectionType,
				orderInWorkoutPlan,
			});

			// 3. Dispatch the action
			dispatch(sectionPlanSliceActions.addedOne(newSection));

			// Mark as dirty
			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: newSection.id,
			};
		} catch (error) {
			return {
				success: false,
				error: `Failed to create section plan: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

/**
 * Updates properties of a section
 *
 * @todo Server Integration:
 * - Call useUpdateSectionPlan mutation (PATCH /api/section-plans/:id)
 * - Handle server response with proper section plan structure
 * - Map server response to Redux state
 * - Handle network errors and retry logic
 *
 * @todo Business Logic:
 * - Validate rounds > 0
 * - Validate rest times are reasonable
 * - Check section name uniqueness within plan (currently we don't check for uniqueness)
 */
const updateSectionPlan = createAsyncThunk<
	ThunkResult<void>,
	{
		sectionPlanId: SectionPlanInfoId;
		// Can't update rounds, that needs to be done through updateSectionPlanRounds
		changes: {
			name?: string;
			notes?: string;
			durationSeconds?: number | null;
			intervalSeconds?: number | null;
		};
	},
	{ state: RootState }
>('features/workoutCreator/updateSectionPlan', async (payload, { dispatch }) => {
	try {
		dispatch(
			sectionPlanSliceActions.updatedOne({
				id: payload.sectionPlanId,
				changes: payload.changes,
			}),
		);

		dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

		return {
			success: true,
			data: undefined,
		};
	} catch (error) {
		return {
			success: false,
			error: `Failed to update section: ${error instanceof Error ? error.message : 'Unknown error'}`,
		};
	}
});

/**
 * Switches the section plan type to a different type
 *
 */
const switchSectionPlanType = createAsyncThunk<
	ThunkResult<void>,
	{ sectionPlanId: SectionPlanInfoId; newSectionType: SectionType },
	{ state: RootState }
>(
	'features/workoutCreator/switchSectionPlanType',
	async ({ sectionPlanId, newSectionType }, { dispatch, getState, rejectWithValue }) => {
		const state = getState();

		const sectionPlan = state.sectionPlans.entities[sectionPlanId];
		if (!sectionPlan) {
			return rejectWithValue({
				success: false,
				error: 'Section plan not found',
			});
		}

		// --- The main logic loop
		try {
			// find all child step plans
			const stepsForThisSection = Object.values(state.stepPlans.entities).filter(
				(step) => step.sectionPlanId === sectionPlanId,
			);

			const stepPlanUpdates: Update<StepPlanInfo, StepPlanInfoId>[] = [];
			const setPlanRemoves: SetPlanInfoId[] = [];

			for (const stepPlan of stepsForThisSection) {
				// We change all repetition modes to template
				// and set the templateSets to the default value for the new section type
				stepPlanUpdates.push({
					id: stepPlan.id,
					changes: {
						repetitionMode: 'template',
						templateSets: newSectionType === 'standard' ? 1 : null,
					},
				});

				// if the previous step plan was in explicit mode, we need to remove all set plans except the first one
				if (stepPlan.repetitionMode === 'explicit') {
					// find all set plans for this step plan that are not the first set
					const setPlansForThisStep = Object.values(state.setPlans.entities)
						.filter((set) => set.stepPlanId === stepPlan.id)
						.filter((set) => set.setNumber !== 1);

					setPlanRemoves.push(...setPlansForThisStep.map((set) => set.id));
				}
			}

			// --- Dispatch State Changes ---
			// 1. remove all set plans that are not the first set
			if (setPlanRemoves.length > 0) {
				dispatch(setPlanSliceActions.removedMany(setPlanRemoves));
			}

			// 2. Update all step plans to template mode
			dispatch(stepPlanSliceActions.updatedMany(stepPlanUpdates));

			// 3. Update the section plan to have the new section type and default input values
			dispatch(
				sectionPlanSliceActions.updatedOne({
					id: sectionPlanId,
					changes: {
						sectionType: newSectionType,
						...sectionTypeDisplayInfos[newSectionType].coreModel.getDefaultInputValues(),
					},
				}),
			);

			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return { success: true, data: undefined };
		} catch (error) {
			return rejectWithValue({
				success: false,
				error: `Failed to switch section plan type: ${error instanceof Error ? error.message : 'Unknown error'}`,
			});
		}
	},
);

/**
 * Updates the rounds of a section and any other properties that are affected by the rounds.
 *
 */
const updateSectionPlanRounds = createAsyncThunk<
	ThunkResult<void>,
	{ sectionPlanId: SectionPlanInfoId; newRounds: number },
	{ state: RootState }
>(
	'features/workoutCreator/updateSectionPlanRounds',
	async ({ sectionPlanId, newRounds }, { dispatch, getState, rejectWithValue }) => {
		const state = getState();

		const sectionPlan = state.sectionPlans.entities[sectionPlanId];
		if (!sectionPlan) {
			return rejectWithValue({
				success: false,
				error: 'Section plan not found',
			});
		}
		if (sectionPlan.sectionType !== 'superset' && sectionPlan.sectionType !== 'circuit') {
			return rejectWithValue({
				success: false,
				error: 'Unable to update rounds for section plan because it is not a superset or circuit',
			});
		}

		// --- The main logic loop
		try {
			// find all child step plans
			const existingSteps = stepPlanSliceSelectors.selectAllBySectionPlanId(state, sectionPlanId);

			// iterate over all child step plans
			for (const stepPlan of existingSteps) {
				// biome-ignore lint/suspicious/noEmptyBlockStatements: this is intentional
				if (stepPlan.repetitionMode === 'template') {
				} else if (stepPlan.repetitionMode === 'explicit') {
					// @todo: i don't like this. above we checked that we're in a 'circuit' or 'superset' so this should be safe
					// and no need to use '??' operator. and assign to zero.
					const currentRounds = sectionPlan.rounds ?? 0;

					// -- SCENARIO 2: EXPANSION
					if (newRounds > currentRounds) {
						const setPlansToAddCount: number = newRounds - currentRounds;

						const { nextSetNumber } = getNextSetPlanData(state, stepPlan.id);

						const newSetPlans = Array.from({ length: setPlansToAddCount }, (_, i) =>
							createNewSetPlan({
								stepPlanId: stepPlan.id,
								setNumber: nextSetNumber + i,
							}),
						);

						// Use batch creation instead of multiple dispatches
						dispatch(setPlanSliceActions.addedMany(newSetPlans));
					}

					// -- SCENARIO 3: TRUNCATION
					else if (newRounds < currentRounds) {
						const setPlansToRemoveCount: number = currentRounds - newRounds;
						const existingSets = setPlanSliceSelectors.selectAllByStepPlanId(state, stepPlan.id);

						const setPlansToRemove = existingSets
							.slice(-1 * setPlansToRemoveCount) // Give me the last n sets
							.map((set) => set.id);

						dispatch(setPlanSliceActions.removedMany(setPlansToRemove));
					}
				}
			}

			// --- Final Update ---
			// After handling all the children, update the parent section's rounds.
			dispatch(
				sectionPlanSliceActions.updatedOne({
					id: sectionPlanId,
					changes: { rounds: newRounds },
				}),
			);

			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return { success: true, data: undefined };
		} catch (error) {
			return rejectWithValue({
				success: false,
				error: `Failed to update section plan rounds: ${error instanceof Error ? error.message : 'Unknown error'}`,
			});
		}
	},
);

/**
 * Removes a section and all its children from the plan
 * it should also reorder the remaining sections
 *
 * @todo Server Integration:
 * - Call useDeleteSectionPlan mutation (DELETE /api/section-plans/:id)
 * - Handle server response with proper section plan structure
 * - Map server response to Redux state
 * - Handle network errors and retry logic
 *
 * @todo Business Logic:
 * - Add confirmation dialog for sections with content
 * - Update order of remaining sections
 * - Cascade delete all child steps and sets
 */
const removeSectionPlan = createAsyncThunk<
	ThunkResult<void>,
	{ sectionPlanId: SectionPlanInfoId },
	{ state: RootState }
>(
	'features/workoutCreator/removeSectionPlan',
	async ({ sectionPlanId }, { dispatch, getState }) => {
		try {
			const state = getState();

			// Find all child steps
			const childSteps = Object.values(state.stepPlans.entities)
				.filter((step) => step.sectionPlanId === sectionPlanId)
				.map((step) => step.id);

			// Find all grandchild sets
			const grandchildSets = Object.values(state.setPlans.entities)
				.filter((set) => childSteps.includes(set.stepPlanId))
				.map((set) => set.id);

			// Remove in correct order: sets → steps → section
			if (grandchildSets.length > 0) {
				dispatch(setPlanSliceActions.removedMany(grandchildSets));
			}
			if (childSteps.length > 0) {
				dispatch(stepPlanSliceActions.removedMany(childSteps));
			}
			dispatch(sectionPlanSliceActions.removedOne(sectionPlanId));

			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: undefined,
			};
		} catch (error) {
			return {
				success: false,
				error: `Failed to remove section: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

// ============================================================================
// Step & Set Management Thunks
// ============================================================================

/**
 * Adds a new exercise step to a section (CORE BUSINESS LOGIC)
 *
 * @todo Server Integration:
 * - No immediate server call (save on draft save)
 * - Validate exercise exists and is accessible
 *
 * @todo Metric Suggestion:
 * - Implement metric suggestion waterfall based on exercise type
 * - Consider user's previous workouts for defaults
 * - Add exercise-specific metric recommendations
 */
const addStepPlan = createAsyncThunk<
	ThunkResult<StepPlanInfoId>,
	{ sectionPlanId: SectionPlanInfoId; exerciseId: ExerciseInfoId },
	{ state: RootState }
>(
	'features/workoutCreator/addStepPlan',
	async (payload, { dispatch, getState, rejectWithValue }) => {
		try {
			const state = getState();

			// Get parent section
			const section = state.sectionPlans.entities[payload.sectionPlanId];
			if (!section) {
				return rejectWithValue({
					success: false,
					error: 'Section not found',
				});
			}

			const { nextStepOrderInSectionPlan, totalSteps } = getNextStepPlanData(
				state,
				payload.sectionPlanId,
			);
			const { sectionType } = section;
			const sectionTypeKey: SectionType = sectionType;
			const { maxSteps } = sectionTypeDisplayInfos[sectionTypeKey].coreModel.rules;

			// Business rule validation
			// @todo: this needs to be a lot more in depth.
			if (totalSteps >= maxSteps) {
				return rejectWithValue({
					success: false,
					error: `${sectionType} sections can only have ${maxSteps} exercise(s)`,
				});
			}

			const newStepPlan = createNewStepPlan({
				exerciseId: payload.exerciseId,
				sectionPlanId: payload.sectionPlanId,
				orderInSectionPlan: nextStepOrderInSectionPlan,
			});

			// Only standard sections should have a template set
			// we initialize this to 1 because we're going to add a set plan right away to this step plan
			if (sectionType === 'standard') {
				newStepPlan.templateSets = 1;
			}

			// --- Dispatch State Changes ---
			dispatch(stepPlanSliceActions.addedOne(newStepPlan));

			// Create template set with default metrics
			const newSetPlan = createNewSetPlan({
				stepPlanId: newStepPlan.id,
				setNumber: 1,
			});
			dispatch(setPlanSliceActions.addedOne(newSetPlan));

			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: newStepPlan.id,
			};
		} catch (error) {
			return {
				success: false,
				error: `Failed to add step: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

/**
 * Adds multiple new exercise steps to a section (CORE BUSINESS LOGIC)
 *
 * @todo Server Integration:
 * - No immediate server call (save on draft save)
 * - Validate exercise exists and is accessible
 *
 * @todo Metric Suggestion:
 * - Implement metric suggestion waterfall based on exercise type
 * - Consider user's previous workouts for defaults
 * - Add exercise-specific metric recommendations
 */
const addStepPlans = createAsyncThunk<
	ThunkResult<StepPlanInfoId[]>,
	{ sectionPlanId: SectionPlanInfoId; exerciseIds: ExerciseInfoId[] },
	{ state: RootState }
>(
	'features/workoutCreator/addStepPlans',
	async ({ sectionPlanId, exerciseIds }, { dispatch, getState, rejectWithValue }) => {
		try {
			const state = getState();

			// Get parent section
			const section = state.sectionPlans.entities[sectionPlanId];
			if (!section) {
				return rejectWithValue({
					success: false,
					error: 'Section not found',
				});
			}

			// Get current step count
			const { nextStepOrderInSectionPlan, totalSteps } = getNextStepPlanData(state, sectionPlanId);
			const { sectionType } = section;
			const sectionTypeKey: SectionType = sectionType;
			const { maxSteps } = sectionTypeDisplayInfos[sectionTypeKey].coreModel.rules;

			// Business rule validation
			// @todo: this needs to be a lot more in depth.
			if (totalSteps >= maxSteps) {
				return rejectWithValue({
					success: false,
					error: `${sectionType} sections can only have ${maxSteps} exercise(s)`,
				});
			}

			const newStepPlans = exerciseIds.map((exerciseId, index) =>
				createNewStepPlan({
					exerciseId,
					sectionPlanId,
					orderInSectionPlan: nextStepOrderInSectionPlan + index,
				}),
			);

			// Only standard sections should have a template set
			// we initialize this to 1 because we're going to add a set plan right away to this step plan
			if (sectionType === 'standard') {
				newStepPlans.forEach((stepPlan) => {
					stepPlan.templateSets = 1;
				});
			}

			// Make one set plan for each step plan
			const newSetPlans = newStepPlans.map((stepPlan) =>
				createNewSetPlan({ stepPlanId: stepPlan.id, setNumber: 1 }),
			);

			// --- Dispatch State Changes ---
			dispatch(stepPlanSliceActions.addedMany(newStepPlans));
			dispatch(setPlanSliceActions.addedMany(newSetPlans));
			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: newStepPlans.map((stepPlan) => stepPlan.id),
			};
		} catch (error) {
			return {
				success: false,
				error: `Failed to add step: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

/**
 * Adds a metric to step tracking
 */
const addTrackedMetricToStepPlan = createAsyncThunk<
	ThunkResult<void>,
	{ stepPlanId: StepPlanInfoId; metricCanonicalName: MetricCanonicalName },
	{ state: RootState }
>(
	'features/workoutCreator/addTrackedMetricToStepPlan',
	async ({ stepPlanId, metricCanonicalName }, { dispatch }) => {
		try {
			dispatch(
				stepPlanSliceActions.metricTracked({
					id: stepPlanId,
					metricCanonicalName,
				}),
			);

			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: undefined,
			};
		} catch (error) {
			return {
				success: false,
				error: `Failed to add tracked metric: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

/**
 * Updates properties of a step plan
 * - templateSets
 * - notes
 */
const updateStepPlanProperties = createAsyncThunk<
	ThunkResult<void>,
	{
		stepPlanId: StepPlanInfoId;
		changes: {
			templateSets?: number;
			notes?: string | null;
		};
	},
	{ state: RootState }
>(
	'features/workoutCreator/updateStepPlanProperties',
	async ({ stepPlanId, changes }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(
				stepPlanSliceActions.updatedOne({
					id: stepPlanId,
					changes,
				}),
			);

			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: undefined,
			};
		} catch (error) {
			console.error(error);
			return rejectWithValue({
				success: false,
				error: `Failed to update step plan properties: ${error instanceof Error ? error.message : 'Unknown error'}, action payload: ${JSON.stringify({ stepPlanId, changes })}`,
			});
		}
	},
);

/**
 * Transitions a single `step_plan` from "Template Mode" to "Explicit Mode".
 *
 * This is a critical, user-initiated action that "unlocks" a step for granular,
 * set-by-set planning. It is a destructive, one-way operation that replaces
 * a simple repetition rule (e.g., "4 sets") with a series of distinct,
 * individually editable set records.
 *
 * ### Core Logic and Business Rules:
 *
 * 1.  **Validation:**
 *     - The thunk first performs a series of guard clauses to ensure the action is valid.
 *     - It checks that the target step and its parent section exist.
 *     - It verifies that the step is currently in `'template'` mode.
 *     - It uses the `sectionTypeDisplayInfos` configuration to check if the section type
 *       (e.g., `amrap`, `emom`) is even eligible for this operation.
 *
 * 2.  **Determine Repetition Count:**
 *     - It uses the `getSetCount` function from the section type model to determine the
 *       number of sets to create.
 *
 * 3.  **State Transformation (The "Fork"):**
 *     - If validation passes, the thunk performs the state transformation.
 *     - It reads the data from the single, existing "template" `set_plan`.
 *     - It dispatches `sets/addMany` to create new, explicit `set_plan` records,
 *       copying the data from the template.
 *     - It then dispatches `steps/updateOne` to change the `step_plan`'s `mode` to `'explicit'`
 *       and set its `templateSets` to `NULL`.
 *     - Finally, it dispatches `workoutCreator/setDirty(true)` to mark the plan as having unsaved changes.
 *
 * This thunk is a perfect example of the "Thick Thunk" pattern, encapsulating complex,
 * multi-step business logic and orchestrating multiple, low-level entity slice updates.
 *
 * @param payload - The payload object.
 * @param payload.stepPlanId - The ID of the `step_plan` to transition.
 * @returns A `ThunkResult` indicating success or failure with a user-friendly error message.
 */
const switchToExplicitRepetitionModeForStepPlan = createAsyncThunk<
	ThunkResult<void>,
	{ stepPlanId: StepPlanInfoId },
	{ state: RootState }
>(
	'features/workoutCreator/switchToExplicitRepetitionModeForStepPlan',

	// @todo: duplicate the template set values for the added new sets
	async ({ stepPlanId }, { dispatch, getState }) => {
		const state = getState();

		// --- 1. Get State & Validate ---
		const stepPlan = state.stepPlans.entities[stepPlanId];
		if (!stepPlan) {
			return { success: false, error: 'Step plan not found' };
		}

		const sectionPlan = state.sectionPlans.entities[stepPlan.sectionPlanId];
		if (!sectionPlan) {
			return { success: false, error: 'Parent section plan not found' };
		}

		if (stepPlan.repetitionMode !== 'template') {
			return { success: false, error: 'Step plan is already in explicit mode' };
		}

		const { sectionType } = sectionPlan;
		const sectionTypeKey: SectionType = sectionType;
		const { coreModel } = sectionTypeDisplayInfos[sectionTypeKey];
		const { supportedRepetitionModesForItsStepPlans } = coreModel;
		if (supportedRepetitionModesForItsStepPlans('explicit') === false) {
			return {
				success: false,
				error: `Can not switch to explicit repetition mode for ${sectionType} section type`,
			};
		}

		// --- 2. Determine Repetition Count ---
		const { getSetCount } = coreModel;
		const existingSetPlans = setPlanSliceSelectors.selectAllByStepPlanId(state, stepPlanId);
		const setCount = getSetCount({
			sectionPlan,
			stepPlan,
			setPlans: existingSetPlans,
		});

		if (setCount === null) {
			return {
				success: false,
				error: `Can not switch to explicit repetition mode for ${sectionType} section type because the set count is not determinable`,
			};
		}

		if (setCount < 1) {
			return {
				success: false,
				error: `Can not switch to explicit repetition mode for ${sectionType} section type because the set count is less than 1: ${setCount}`,
			};
		}

		const templateSet = existingSetPlans.find((set) => set.setNumber === 1);
		if (!templateSet) {
			return {
				success: false,
				error: `Can not switch to explicit repetition mode for ${sectionType} section type because the template set is not found`,
			};
		}

		// --- 3. Dispatch State Changes ---
		try {
			// Create additional sets (setCount - 1 because we already have the template set)
			if (setCount > 1) {
				const additionalSetPlans = Array.from({ length: setCount - 1 }, (_, i) =>
					createNewSetPlan({
						stepPlanId,
						// +2 because we already have the template set
						setNumber: i + 2,
					}),
				);

				dispatch(setPlanSliceActions.addedMany(additionalSetPlans));
			}

			// 2. Update step plan to explicit mode
			dispatch(
				stepPlanSliceActions.updatedOne({
					id: stepPlanId,
					changes: { repetitionMode: 'explicit', templateSets: null },
				}),
			);

			// 3. Set dirty flag
			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return { success: true, data: undefined };
		} catch (error) {
			console.error(error);
			return {
				success: false,
				error: `Failed to switch to explicit repetition mode for step plan: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

/**
 * Transitions a single `step_plan` from "Explicit Mode" back to "Template Mode".
 *
 * This is a destructive user action that simplifies a step's set structure. It takes a list
 * of potentially unique, explicit `set_plan` records and collapses them into a single
 * "template" `set_plan`. The target values of the first set in the list are preserved
 * as the new template, and all other sets are deleted.
 *
 * ### Core Logic and Business Rules:
 *
 * 1.  **Validation:**
 *     - The thunk first performs guard clauses to ensure the action is valid.
 *     - It checks that the target step and its parent section exist.
 *     - It verifies that the step is currently in `'explicit'` mode.
 *     - It uses the `sectionTypeModels` configuration to check if the section type
 *       supports `'template'` mode for its steps.
 *
 * 2.  **Determine New `templateSets` Count:**
 *     - It counts the number of existing explicit `set_plan` records for the step. This
 *       count becomes the new `templateSets` value for the step.
 *
 * 3.  **State Transformation (The "Collapse"):**
 *     - If validation passes, the thunk performs the state transformation.
 *     - It identifies all explicit `set_plan` records associated with the step, preserving
 *       the first one (sorted by `setNumber`) as the new template.
 *     - It dispatches `sets/removedMany` with the IDs of all the other, now-redundant sets.
 *     - It dispatches `steps/updatedOne` to change the `step_plan`'s `mode` to `'template'`
 *       and set its `templateSets` to the new count.
 *     - Finally, it dispatches `workoutCreator/setDirty(true)` to mark the plan as having unsaved changes.
 *
 * @param payload - The payload object.
 * @param payload.stepPlanId - The ID of the `step_plan` to transition.
 * @returns A `ThunkResult` indicating success or failure.
 */
const switchToTemplateRepetitionModeForStepPlan = createAsyncThunk<
	ThunkResult<void>, // return type
	{ stepPlanId: StepPlanInfoId }, // payload type
	{ state: RootState }
>(
	'features/workoutCreator/switchToTemplateRepetitionModeForStepPlan',
	async ({ stepPlanId }, { dispatch, getState, rejectWithValue }) => {
		const state = getState();

		// --- 1. Get State & Validate ---
		const stepPlan = state.stepPlans.entities[stepPlanId];
		if (!stepPlan) {
			return { success: false, error: 'Step plan not found' };
		}

		const sectionPlan = state.sectionPlans.entities[stepPlan.sectionPlanId];
		if (!sectionPlan) {
			return { success: false, error: 'Parent section plan not found' };
		}

		if (stepPlan.repetitionMode !== 'explicit') {
			return { success: false, error: 'Step plan is not in explicit mode' };
		}

		const { sectionType } = sectionPlan;
		const sectionTypeKey: SectionType = sectionType;
		const { coreModel } = sectionTypeDisplayInfos[sectionTypeKey];
		const { supportedRepetitionModesForItsStepPlans } = coreModel;
		if (supportedRepetitionModesForItsStepPlans('template') === false) {
			return {
				success: false,
				error: `Can not switch to template repetition mode for ${sectionType} section type. It does not support template repetition mode for its step plans`,
			};
		}

		// --- 2. Determine New `templateSets` Count and Identify the set plans to remove ---
		const explicitSetPlans = setPlanSliceSelectors.selectAllByStepPlanId(state, stepPlanId);
		const totalSetCount = explicitSetPlans.length;

		// @todo: this should never happen. i don't know what should happen in this case?
		if (totalSetCount === 0) {
			return rejectWithValue({
				success: false,
				error: `Can not switch to template repetition mode for ${sectionType} section type because the explicit set plans are not found`,
			});
		}

		const firstSetPlan = explicitSetPlans[0];
		if (!firstSetPlan) {
			return rejectWithValue({
				success: false,
				error: `Can not switch to template repetition mode for ${sectionType} section type because the first set plan is not found`,
			});
		}
		const setsToRemove = explicitSetPlans.slice(1).map((set) => set.id);

		// --- 3. Dispatch State Changes ---
		try {
			// 1. Remove the redundant set plans, if any
			if (setsToRemove.length > 0) {
				dispatch(setPlanSliceActions.removedMany(setsToRemove));
			}

			// 2. Update step plan to template mode with the new count
			dispatch(
				stepPlanSliceActions.updatedOne({
					id: stepPlanId,
					changes: {
						repetitionMode: 'template',
						// @todo: i'm not sure if this is correct.
						templateSets: sectionType === 'standard' ? totalSetCount : null,
					},
				}),
			);

			// 3. (Optional but good practice) Ensure the remaining set plan has setNumber = 1
			if (firstSetPlan.setNumber !== 1) {
				dispatch(
					setPlanSliceActions.updatedOne({
						id: firstSetPlan.id,
						changes: { setNumber: 1 },
					}),
				);
			}

			// 4. Set dirty flag
			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return { success: true, data: undefined };
		} catch (error) {
			console.error(error);
			return {
				success: false,
				error: `Failed to switch to template repetition mode for step plan: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

/**
 * Removes a metric from step tracking (automatically clears set target metric value as well)
 */
const removeTrackedMetricFromStepPlan = createAsyncThunk<
	ThunkResult<void>,
	{ stepPlanId: StepPlanInfoId; metricCanonicalName: MetricCanonicalName },
	{ state: RootState }
>(
	'features/workoutCreator/removeTrackedMetricFromStepPlan',
	async ({ stepPlanId, metricCanonicalName }, { dispatch }) => {
		try {
			// This will automatically clear corresponding set values via extraReducers
			dispatch(
				stepPlanSliceActions.metricUntracked({
					id: stepPlanId,
					metricCanonicalName,
				}),
			);

			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: undefined,
			};
		} catch (error) {
			return {
				success: false,
				error: `Failed to remove tracked metric: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

/**
 * Removes a step and its sets from a section
 *
 * @todo Server Integration:
 * - Call useDeleteStepPlan mutation (DELETE /api/step-plans/:id)
 * - Handle server response with proper step plan structure
 * - Map server response to Redux state
 * - Handle network errors and retry logic
 *
 * @todo Business Logic:
 * - Add confirmation dialog for steps with target metric data
 * - Update order of remaining steps in section
 * - Consider soft delete vs hard delete
 * - Cascade delete all child sets
 */
const removeStepPlan = createAsyncThunk<
	ThunkResult<void>,
	{ stepPlanId: StepPlanInfoId },
	{ state: RootState }
>('features/workoutCreator/removeStepPlan', async ({ stepPlanId }, { dispatch, getState }) => {
	try {
		const state = getState();

		// Find all child sets for this step
		const childSetIds = setPlanSliceSelectors
			.selectAllByStepPlanId(state, stepPlanId)
			.map((set: SetPlanInfo) => set.id);

		// 1. Remove sets first, then step (cascade delete)
		if (childSetIds.length > 0) {
			dispatch(setPlanSliceActions.removedMany(childSetIds));
		}

		// 2. Remove step after the sets are removed
		dispatch(stepPlanSliceActions.removedOne(stepPlanId));

		// 3. Set dirty flag
		dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

		return {
			success: true,
			data: undefined,
		};
	} catch (error) {
		return {
			success: false,
			error: `Failed to remove step plan: ${error instanceof Error ? error.message : 'Unknown error'}`,
		};
	}
});

/**
 * Updates a single target metric value for a set (e.g., weight, reps, distance)
 *
 * @todo Server Integration:
 * - Call useUpdateSetPlanMetric mutation (PATCH /api/set-plans/:id/metrics)
 * - Handle server response with proper set plan structure
 * - Consider debounced saves for frequent metric input updates
 * - Handle network errors and retry logic
 *
 * @todo Business Logic:
 * - Add validation for metric value ranges (e.g., weight > 0, reps > 0)
 * - Check metric compatibility with parent step's exercise type
 * - Add metric unit conversion support
 * - Consider optimistic updates for better UX
 */
const updateSetPlanTargetMetric = createAsyncThunk<
	ThunkResult<void>,
	{
		setPlanId: SetPlanInfoId;
		metricCanonicalName: MetricCanonicalName;
		value: number | null;
	},
	{ state: RootState }
>(
	'features/workoutCreator/updateSetPlanTargetMetric',
	async ({ setPlanId, metricCanonicalName, value }, { dispatch }) => {
		try {
			// Handle null values by clearing the metric, non-null by setting it
			if (value === null) {
				// @todo: Need a clearMetricValue action in set slice for null values
				// For now, we'll set to 0 or handle differently
				console.warn('Null metric values not yet supported, setting to 0');
				dispatch(
					setPlanSliceActions.metricValueUpdated({
						setPlanId,
						metricCanonicalName,
						value: 0,
					}),
				);
			} else {
				dispatch(
					setPlanSliceActions.metricValueUpdated({
						setPlanId,
						metricCanonicalName,
						value,
					}),
				);
			}

			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: undefined,
			};
		} catch (error) {
			console.error(error);
			return {
				success: false,
				error: `Failed to update set plan target metric: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

/**
 * Updates other set properties (not target metrics)
 *
 * @todo Server Integration:
 * - Call useUpdateSetPlan mutation (PATCH /api/set-plans/:id)
 * - Handle server response with proper set plan structure
 * - Map server response to Redux state
 * - Handle network errors and retry logic
 *
 * @todo Business Logic:
 * - Add validation for set properties (e.g., setNumber > 0)
 * - Consider what other properties sets might have in the future
 * - Add property-specific validation rules
 */
const updateSetPlanProperties = createAsyncThunk<
	ThunkResult<void>,
	{
		setPlanId: SetPlanInfoId;
		changes: {
			setNumber?: number;
			trackTime?: boolean;
		};
	},
	{ state: RootState }
>(
	'features/workoutCreator/updateSetPlanProperties',
	async ({ setPlanId, changes }, { dispatch }) => {
		try {
			dispatch(
				setPlanSliceActions.updatedOne({
					id: setPlanId,
					changes,
				}),
			);

			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: undefined,
			};
		} catch (error) {
			console.error(error);
			return {
				success: false,
				error: `Failed to update set plan properties: ${error instanceof Error ? error.message : 'Unknown error'}`,
			};
		}
	},
);

/**
 * Updates properties of a step in a section (not currently needed )
 *
 * @todo Implementation Needed:
 * - First need to add a general stepUpdated action to step slice
 * - Or create specific actions for each step property that needs updating
 *
 * @todo Server Integration:
 * - Call useUpdateStepPlan mutation (PATCH /api/step-plans/:id)
 * - Handle server response with proper step plan structure
 * - Map server response to Redux state
 * - Handle network errors and retry logic
 *
 * @todo Business Logic:
 * - Validate order changes don't create conflicts within section
 * - Check tracked metrics are valid for the exercise type
 * - Add step reordering with drag-and-drop support
 * - Validate step configuration is complete before allowing updates
 */
// @todo: Implement this thunk after adding stepUpdated action to step slice
// export const updateStepInSection = createAsyncThunk<
//   ThunkResult<void>,
//   UpdateStepInSectionPayload,
//   { state: RootState }
// >(
//   'features/workoutCreator/updateStepInSection',
//   async (payload, { dispatch }) => {
//     try {
//       dispatch(stepPlanSliceActions.stepUpdated({
//         id: payload.stepId,
//         changes: payload.changes,
//       }));

//       dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

//       return {
//         success: true,
//         data: undefined,
//       };
//     }
//     catch (error) {
//       return {
//         success: false,
//         error: `Failed to update step: ${error instanceof Error ? error.message : 'Unknown error'}`,
//       };
//     }
//   },
// );

/**
 * Removes a set plan from a step plan (must be in explicit repetition mode)
 *
 */
const removeSetPlan = createAsyncThunk<
	ThunkResult<void>,
	{ setPlanId: SetPlanInfoId },
	{ state: RootState }
>('features/workoutCreator/removeSetPlan', async ({ setPlanId }, { dispatch, getState }) => {
	try {
		const state = getState();

		// Find the set plan
		const setPlan = state.setPlans.entities[setPlanId];
		if (!setPlan) {
			// actually we can return success: true because the set plan is already removed
			console.warn(`Set plan not found: ${setPlanId}`);
			return {
				success: true,
				data: undefined,
			};
		}

		const parentStepPlan = state.stepPlans.entities[setPlan.stepPlanId];
		// This is impossible state, log a warning but go ahead and remove the set plan
		if (!parentStepPlan) {
			console.warn(`Step plan not found: ${setPlan.stepPlanId}`);
		}

		if (parentStepPlan && parentStepPlan.repetitionMode !== 'explicit') {
			console.error('Can not remove set plan from step plan in non-explicit repetition mode');
			return {
				success: false,
				error: 'Can not remove set plan from step plan in non-explicit repetition mode',
			};
		}

		// @todo: should we do sth about the setplan.setNumber?
		dispatch(setPlanSliceActions.removedOne(setPlanId));

		dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

		return {
			success: true,
			data: undefined,
		};
	} catch (error) {
		return {
			success: false,
			error: `Failed to remove set plan: ${error instanceof Error ? error.message : 'Unknown error'}`,
		};
	}
});

/**
 * Adds a new set plan to a step plan in explicit repetition mode
 * Only works for steps in explicit mode
 */
const addSetPlan = createAsyncThunk<
	ThunkResult<SetPlanInfoId>,
	{ stepPlanId: StepPlanInfoId },
	{ state: RootState }
>(
	'features/workoutCreator/addSetPlan',
	async ({ stepPlanId }, { dispatch, getState, rejectWithValue }) => {
		try {
			const state = getState();

			// Get the step plan
			const stepPlan = state.stepPlans.entities[stepPlanId];
			if (!stepPlan) {
				return rejectWithValue({
					success: false,
					error: 'Step plan not found',
				});
			}

			// Get the parent section
			const section = state.sectionPlans.entities[stepPlan.sectionPlanId];
			if (!section) {
				return rejectWithValue({
					success: false,
					error: 'Section plan not found',
				});
			}

			if (section.sectionType !== 'standard') {
				return rejectWithValue({
					success: false,
					error:
						'Can only add set plans to step plans individually that are in standard section type',
				});
			}

			// Validate that step is in explicit mode
			if (stepPlan.repetitionMode !== 'explicit') {
				return rejectWithValue({
					success: false,
					error:
						'Can only add set plans to step plans individually that are in explicit repetition mode',
				});
			}

			// Create the set with correct set number from the start
			const { nextSetNumber } = getNextSetPlanData(state, stepPlanId);
			const newSetPlan = createNewSetPlan({
				stepPlanId,
				setNumber: nextSetNumber,
			});

			// --- Dispatch State Changes ---

			// 1. Add the set plan
			dispatch(setPlanSliceActions.addedOne(newSetPlan));

			// 2. Set dirty flag
			dispatch(workoutCreatorSliceActions.dirtyFlagSet(true));

			return {
				success: true,
				data: newSetPlan.id,
			};
		} catch (error) {
			console.error('Failed to add set plan to step plan:', error);
			return rejectWithValue({
				success: false,
				error: 'Failed to add set plan to step plan',
			});
		}
	},
);

const workoutCreatorThunks = {
	startNewWorkoutPlan,
	updateWorkoutPlanProperties,
	saveDraftWorkoutPlan,
	publishWorkoutPlan,

	addSectionPlan,
	updateSectionPlan,
	updateSectionPlanRounds,
	switchSectionPlanType,
	removeSectionPlan,

	addStepPlan,
	addStepPlans,
	addTrackedMetricToStepPlan,
	updateStepPlanProperties,
	switchToExplicitRepetitionModeForStepPlan,
	switchToTemplateRepetitionModeForStepPlan,
	removeTrackedMetricFromStepPlan,
	removeStepPlan,

	addSetPlan,
	updateSetPlanTargetMetric,
	updateSetPlanProperties,
	removeSetPlan,
};

export { workoutCreatorThunks };
